import React, { useEffect, useState } from "react"
import { Dropdown, Tooltip } from "antd"
import type { MenuProps } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faGripVertical,
  faEllipsis,
  faRotateRight,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons"
import { QC_BUILDER_DRAG_TYPE } from "stores/queryBuilderStore"
import ConfirmationModal from "shared/ui/ConfimationModal/ConfimationModal.component"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { Tile, isDataTile } from "features/Dashboard/types/dashboardDocument"
import {
  useDashboardStore,
  selectAnyFilterActive,
} from "features/Dashboard/runtime/dashboardStore"
import TileBody from "./TileBody.component"
import TileControls from "./TileControls.component"

export const TYPE_LABEL: Record<Tile["type"], string> = {
  kpi: "Number",
  bar: "Bar chart",
  line: "Line chart",
  pie: "Pie chart",
  table: "Table",
  scorecard: "Scorecard",
  text: "Text",
  image: "Image",
}

/**
 * What a data tile shows while the dashboard's preparation has failed: in
 * Build mode, where the Data panel opens, and in View mode, where it does
 * not.
 */
export const PREP_FAILED_TILE = "Preparation failed, fix it in the Data panel"
export const PREP_FAILED_TILE_VIEW =
  "The data preparation failed. Switch to Build to fix it in the Data panel."
/** The hover text of the "AI" tag on a tile the assistant added or changed. */
export const AI_TAG_TITLE =
  "The assistant added or changed this tile. Save the dashboard to keep it, or Undo on the proposal to take it back."

/**
 * The frame around every tile: a header (drag handle, title, an "AI" tag
 * while the assistant's change to it is unsaved, run status, exposed
 * controls and the Build-mode menu) over the tile's body, with the
 * error, loading and selected states. While the preparation has failed,
 * a data tile says so instead of waiting on a run. In Build mode a click
 * selects the tile, the Delete key removes the selected tile after a
 * confirm, and a variable dropped on a builder tile becomes its rows (when
 * it has none) or one more column.
 */
const TileChrome: React.FC<{ tile: Tile; pageId: string }> = ({
  tile,
  pageId,
}) => {
  const mode = useDashboardStore((s) => s.mode)
  const runtime = useDashboardStore((s) => s.tiles[tile.id])
  const prepFailed = useDashboardStore(
    (s) => s.session.status === "prep-error"
  )
  const selected = useDashboardStore((s) => s.selectedTileId === tile.id)
  const ai = useDashboardStore((s) => s.aiTileIds.includes(tile.id))
  const anyFilter = useDashboardStore(selectAnyFilterActive)
  const selectTile = useDashboardStore((s) => s.selectTile)
  const removeTile = useDashboardStore((s) => s.removeTile)
  const duplicateTile = useDashboardStore((s) => s.duplicateTile)
  const retryTile = useDashboardStore((s) => s.retryTile)
  const updateTile = useDashboardStore((s) => s.updateTile)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [slow, setSlow] = useState(false)
  const build = mode === "build"
  const data = isDataTile(tile)
  // No data tile runs until the preparation is fixed.
  const blocked = data && prepFailed
  const acceptsVariable =
    build && isDataTile(tile) && tile.source.kind === "builder"
  // Only data tiles run: an edit marks a text or image tile stale too, but
  // nothing ever runs it.
  const busy =
    data &&
    !blocked &&
    (runtime?.status === "queued" ||
      runtime?.status === "running" ||
      runtime?.status === "stale")

  useEffect(() => {
    if (!busy) {
      setSlow(false)
      return
    }
    const t = setTimeout(() => setSlow(true), 2000)
    return () => clearTimeout(t)
  }, [busy])

  useEffect(() => {
    if (!build || !selected) return
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      const tag = target?.tagName
      if (
        e.key === "Delete" &&
        tag !== "INPUT" &&
        tag !== "TEXTAREA" &&
        !target?.isContentEditable
      ) {
        setConfirmDelete(true)
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [build, selected])

  const menu: MenuProps["items"] = [
    ...(data
      ? [{ key: "run", label: "Run", onClick: () => retryTile(tile.id) }]
      : []),
    {
      key: "duplicate",
      label: "Duplicate",
      onClick: () => duplicateTile(pageId, tile.id),
    },
    { type: "divider" as const },
    {
      key: "delete",
      label: "Delete",
      danger: true,
      onClick: () => setConfirmDelete(true),
    },
  ]

  // No stopPropagation: the grid counts drag enters and leaves, and a drop
  // it never sees would leave its counter stuck.
  const onDrop = (e: React.DragEvent) => {
    if (!acceptsVariable) return
    const variable = e.dataTransfer.getData(QC_BUILDER_DRAG_TYPE)
    if (!variable) return
    e.preventDefault()
    updateTile(pageId, tile.id, (t) => {
      if (!isDataTile(t) || t.source.kind !== "builder") return t
      const src = t.source
      if (src.rows.includes(variable) || src.columns.includes(variable)) {
        return t
      }
      return {
        ...t,
        source:
          src.rows.length === 0
            ? { ...src, rows: [variable] }
            : { ...src, columns: [...src.columns, variable] },
      }
    })
  }

  const label = tile.title || TYPE_LABEL[tile.type]

  return (
    <section
      className="dq-tile"
      data-selected={build && selected}
      data-status={runtime?.status ?? "idle"}
      aria-label={label}
      onClick={(e) => {
        // Clicks inside the menu or the confirm dialog reach here through
        // their portals; they must not select (or re-select) the tile.
        if (!build || !e.currentTarget.contains(e.target as Node)) return
        e.stopPropagation()
        selectTile(tile.id)
      }}
      onDragOver={(e) => {
        if (
          acceptsVariable &&
          e.dataTransfer.types.includes(QC_BUILDER_DRAG_TYPE)
        ) {
          e.preventDefault()
        }
      }}
      onDrop={onDrop}
    >
      <header className={`dq-tile__header ${build ? "dq-tile-handle" : ""}`}>
        {build && (
          <FontAwesomeIcon
            icon={faGripVertical}
            className="dq-tile__grip"
            aria-hidden
          />
        )}
        <span className="dq-tile__title">{label}</span>
        {ai && (
          <Tooltip title={AI_TAG_TITLE}>
            <span className="dq-tile__badge" data-tone="ai" aria-label={AI_TAG_TITLE}>
              AI
            </span>
          </Tooltip>
        )}
        {busy && (
          <span
            className="dq-tile__dot"
            role="img"
            aria-label="Running"
            title={slow ? "Still running" : "Running"}
            data-slow={slow}
          />
        )}
        {data && runtime && !runtime.usesUniverse && anyFilter && (
          <Tooltip title="This script has no {{universe}} token, so filters do not apply to it.">
            <span className="dq-tile__badge">Unfiltered</span>
          </Tooltip>
        )}
        {data && (
          <div className="dq-tile__controls dq-tile-nodrag">
            <TileControls tile={tile} />
          </div>
        )}
        {build && (
          <Dropdown menu={{ items: menu }} trigger={["click"]}>
            <button
              type="button"
              className="dq-btn dq-btn--icon dq-btn--quiet dq-tile__menu dq-tile-nodrag"
              aria-label="Tile menu"
              onClick={(e) => e.stopPropagation()}
            >
              <FontAwesomeIcon icon={faEllipsis} />
            </button>
          </Dropdown>
        )}
      </header>
      {/* A repeated tile's panels dim one by one while they wait. */}
      <div
        className="dq-tile__body dq-tile-nodrag"
        data-dimmed={busy && Boolean(runtime?.model)}
      >
        {blocked ? (
          <p className="dq-tile__empty dq-tile__message">
            {build ? PREP_FAILED_TILE : PREP_FAILED_TILE_VIEW}
          </p>
        ) : runtime?.status === "error" ? (
          <div className="dq-tile__error" role="alert">
            <FontAwesomeIcon icon={faTriangleExclamation} />
            <p>{runtime.error}</p>
            <DataQButton type="secondary" onClick={() => retryTile(tile.id)}>
              <FontAwesomeIcon icon={faRotateRight} />
              Retry
            </DataQButton>
          </div>
        ) : data && !runtime?.model && !runtime?.panels ? (
          <div className="dq-tile__skeleton" aria-busy="true" />
        ) : (
          <TileBody tile={tile} runtime={runtime} />
        )}
      </div>
      <ConfirmationModal
        title="Delete tile"
        isVisible={confirmDelete}
        message="Delete this tile?"
        confirmButtonText="Delete"
        confirmButtonType="danger"
        cancel={() => setConfirmDelete(false)}
        confirm={() => {
          removeTile(pageId, tile.id)
          setConfirmDelete(false)
        }}
      />
    </section>
  )
}

export default TileChrome
